import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import JSZip from 'jszip';
import { DateTime } from 'luxon';

import { useToast, useModal } from '@pcmp/react-providers';
import Button from '@ingka/button';
import trashcanIcon from '@ingka/ssr-icon/paths/trash-can';
import ToggleControlWrapper from '@ingka/toggle';

import DeleteQrPrompt from '@/components/modals/delete-qr-prompt';
import {
  CodeV2 as Code,
  useDeleteV2QrCodesBySlugMutation,
} from '@/redux/apis/codes-api';
import { useGetScanLink } from '@/lib/hooks/qrcode';
import {
  saveFile,
  saveSvgAsPng,
  saveSvgAsSvg,
  svgToPngBlob,
  svgToSvgBlob,
} from '@/lib/utils/file';
import { api } from '@/redux/apis/images-api';
import {
  useGetV1ConfigQuery,
  useGetV1UsersMeQuery,
} from '@/redux/apis/frontend-api';
import { useDispatch } from 'react-redux';
import { Dispatch } from '@reduxjs/toolkit';
type DownloadFormat = 'SVG' | 'PNG';

const DOWNLOAD_FORMATS: DownloadFormat[] = ['PNG', 'SVG'];
const BLOB_FORMATS_HANDLERS: Record<
  DownloadFormat,
  (svg: SVGElement) => string | Promise<string>
> = {
  PNG: svgToPngBlob,
  SVG: svgToSvgBlob,
};
const DOWNLOAD_FORMATS_HANDLERS: Record<
  DownloadFormat,
  (svg: SVGElement, filename: string) => void
> = {
  PNG: saveSvgAsPng,
  SVG: saveSvgAsSvg,
};

const DeleteButton: FC<{ selectedCodes: Code[] }> = ({ selectedCodes }) => {
  // hooks
  const { t } = useTranslation();
  const { pushToast } = useToast();
  const { openModal } = useModal();
  const [deleteQrCode] = useDeleteV2QrCodesBySlugMutation();
  const { data: currentUser } = useGetV1UsersMeQuery();
  const { data: configData } = useGetV1ConfigQuery();

  const showNotificationDisclaimer = selectedCodes.some(
    (code) => code.user.email !== currentUser?.username
  );

  const forceViewerMode =
    configData?.maintenanceStatus?.enabled &&
    configData?.maintenanceStatus?.forceViewerMode;

  // handlers
  const handleDeleteCodeButton = async () => {
    openModal({
      component: DeleteQrPrompt,
      props: {
        confirm: confirmDeleteQrCode,
        isBulkDelete: selectedCodes.length > 1,
        showNotificationDisclaimer,
      },
    });
  };
  const confirmDeleteQrCode = async () => {
    let count = 0;
    for (const code of selectedCodes) {
      const response = await deleteQrCode({
        slug: code.slug as string,
      });
      const isError = response && 'error' in response;
      if (!isError) count++;
    }
    const message = t(`page.codes.detail.messages.deleteCodes`, {
      count: count,
      total: selectedCodes.length,
    });
    // @ts-ignore
    pushToast({ message });
  };

  return (
    <Button
      data-testid="deleteCodes"
      iconPosition="leading"
      text={t('label.delete')}
      type="secondary"
      size="small"
      onClick={handleDeleteCodeButton}
      ssrIcon={trashcanIcon}
      disabled={forceViewerMode}
    />
  );
};

function parseSvg(imageData: string): SVGSVGElement {
  const parser = new DOMParser();
  const doc = parser.parseFromString(imageData, 'image/svg+xml');

  const svg = doc.querySelector('svg');

  if (!(svg instanceof SVGSVGElement)) {
    throw new Error('Parsed SVG is not a valid <svg> element.');
  }

  return svg;
}

const getCodeSvg = async (dispatch: Dispatch<any>, code: Code) => {
  // @ts-ignore // TODO why does it say data doesn't exist?
  const { data: imageData } = await dispatch(
    api.endpoints.getV1QrCodesBySlugAndNameExt.initiate({
      slug: code.slug!,
      name: `${code.codeName}-${code.icon}`,
      ext: 'svg',
    })
  );
  return parseSvg(imageData);
};

const getQrImage = async (
  dispatch: Dispatch<any>,
  code: Code,
  format: DownloadFormat
) => {
  const svg = await getCodeSvg(dispatch, code);
  return await BLOB_FORMATS_HANDLERS[format](svg);
};

const downloadCodes = async (
  dispatch: Dispatch<any>,
  codes: Code[],
  format: DownloadFormat
) => {
  const zip = new JSZip();
  if (codes.length == 1) {
    const code = codes[0];
    const svg = await getCodeSvg(dispatch, code);
    if (svg) {
      DOWNLOAD_FORMATS_HANDLERS[format](svg, code.codeName);
    }
  } else {
    const qrImages = await Promise.all(
      codes.map((code) => {
        return getQrImage(dispatch, code, format);
      })
    );
    const options = { base64: format === 'PNG' };
    qrImages.forEach((blob, index) => {
      const name = `${codes[index].codeName}.${format.toLowerCase()}`;
      zip.file(name, blob, options);
    });
    await zip
      .generateAsync({
        type: 'blob',
        streamFiles: true,
      })
      .then(function (content) {
        const timeStamp = DateTime.now().toFormat('yyyyMMddHHmmss');
        saveFile(content, `qrcodes_${timeStamp}.zip`);
      });
  }
};

const DownloadButton: FC<{ selectedCodes: Code[] }> = ({ selectedCodes }) => {
  const [formatIndex, setFormatIndex] = useState(0);
  const format = DOWNLOAD_FORMATS[formatIndex];
  const dispatch = useDispatch<any>();

  // hooks
  const { t } = useTranslation();
  const getScanLink = useGetScanLink();

  return (
    <div className="flex">
      <ToggleControlWrapper
        className="flex-grow"
        data-testid="downloadFormatToggle"
        onClick={(_, index) => setFormatIndex(index)}
        activeIndex={formatIndex}
        buttons={DOWNLOAD_FORMATS.map((format) => ({ text: format }))}
        fluid
      />
      <Button
        data-testid="downloadCodes"
        iconPosition="leading"
        text={t('label.download')}
        type="primary"
        size="small"
        onClick={() => downloadCodes(dispatch, selectedCodes, format)}
        className="ms-4"
      />
    </div>
  );
};

export const ActionBar: FC<{ selectedCodes: Code[] }> = ({ selectedCodes }) => {
  return (
    <div className="flex w-full justify-between items-center mt-7 mb-2">
      <DownloadButton selectedCodes={selectedCodes} />
      <DeleteButton selectedCodes={selectedCodes} />
    </div>
  );
};
